import React, {useState} from 'react';
import {FormModal} from 'components/modals';
import {Form} from 'react-bootstrap';
import Button from '@/components/ui/Button';
import axios from 'axios';
import * as yup from 'yup';
import {useTranslation} from 'react-i18next';
import i18n from 'i18n';

interface ModalProps {
    show: boolean;
    onHide: () => void;
}

interface NewStoryboardModalProps {
    onSave: (data: any) => void;
    storyId: number;
    modalProps: ModalProps;
}

interface FormData {
    title: string;
}

const NewStoryboardModalSchema = yup.object({
    title: yup.string()
        .max(255, i18n.t('jt_max_title_length.text'))
        .required(i18n.t('jt_required_field_with_specific_field.text')),
});

const NewStoryboardModal: React.FC<NewStoryboardModalProps> = ({onSave, storyId, modalProps}) => {
    const {t} = useTranslation();
    const [title, setTitle] = useState<string>('');
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<string[] | null>(null);

    const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setTitle(e.target.value);
    };

    const handleSave = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        setSubmitted(true);
        setValidationErrors(null);

        try {
            await NewStoryboardModalSchema.validate({title}, {abortEarly: false});

            const response = await axios.post(window.URLS.storyboards, {
                title,
                storyId,
            });

            onSave(response.data);
            setSubmitted(false);
            setTitle('');
        } catch (err) {
            if (err instanceof yup.ValidationError) {
                setValidationErrors(err.errors);
            }
        }
    };

    return (
        <FormModal
            isOpen={modalProps.show}
            onClose={modalProps.onHide}
            title={i18n.t('jt_create_new.text')}
            maxWidth="6"
        >
            <Form onSubmit={handleSave}>
                <Form.Group className="mb-3" controlId="storyboardTitle" >
                    <Form.Label>{i18n.t('jt_storyboard_title.text')}</Form.Label>
                    <Form.Control
                        type="text"
                        name="storyboardTitle"
                        onChange={onTitleChange}
                        autoFocus
                        maxLength={255}
                        isInvalid={submitted && (!title || !!validationErrors)}
                        data-cy="storyboard-title-input"
                    />
                    {submitted && (!title || validationErrors) && (
                        <Form.Control.Feedback type="invalid">
                            {validationErrors ? validationErrors.join(', ') : i18n.t('jt_notification_title_required.text')}
                        </Form.Control.Feedback>
                    )}
                </Form.Group>

                <Form.Group className="mb-3" controlId="storyboardTitle-save" >
                    <Button
                        variant="info"
                        fullWidth
                        className="storyboard-save-btn"
                        onClick={handleSave}
                        data-cy="storyboard-save-btn"
                    >
                        {i18n.t('jt_save.text')}
                    </Button>
                </Form.Group>
            </Form >
        </FormModal >
    );
};

export default NewStoryboardModal; 